/*
 * 技能 name(slug) → display_name(中文) 映射缓存
 *
 * 1:1 对齐 XHeyClaw-Web/lib/skill-display-cache.ts 的语义：
 *   - 模块级单例 cache，按 groupId 分桶
 *   - 5 分钟 TTL，过期后再 fire prefetch；多次并发调用复用同一 inflight
 *   - 组件首次渲染拿不到时 fallback 到 raw slug，prefetch 完成后 forceUpdate
 *
 * 走 xheyAi.listSkills（→ /xhey-ai/skills?group_id=xxx），由 api.ts 的
 * request() 统一注入 Authorization Bearer + x-group-id + 401 重换 token，
 * 不再裸 fetch 漏 header。
 */

import { xheyAi } from '../../api'

const TTL_MS = 5 * 60 * 1000

let cache: Record<string, string> = {}
let loadedGroupId: string | null = null
let loadedAt = 0
let inflight: Promise<void> | null = null

export function getSkillDisplayName(name: string): string | undefined {
  return cache[name]
}

interface SkillRow {
  name: string
  display_name?: string
}

function applySnapshot(groupId: string, skills: SkillRow[]): void {
  if (loadedGroupId !== groupId) {
    cache = {}
    loadedGroupId = groupId
  }
  for (const s of skills) {
    if (s && s.name) {
      const display = (s.display_name && s.display_name.trim()) || s.name
      cache[s.name] = display
    }
  }
}

/** 幂等 prefetch；返回 promise 给调用方 await 后 forceUpdate */
export function prefetchSkillCache(groupId: string): Promise<void> {
  if (!groupId) return Promise.resolve()
  const fresh =
    loadedGroupId === groupId && loadedAt > 0 && Date.now() - loadedAt < TTL_MS
  if (fresh) return Promise.resolve()
  if (inflight) return inflight
  inflight = xheyAi
    .listSkills(groupId)
    .then((skills) => {
      applySnapshot(groupId, skills as SkillRow[])
      loadedAt = Date.now()
    })
    .catch(() => {
      // 静默失败：拿不到映射就 fallback 到 raw slug，不阻塞 UI
    })
    .finally(() => {
      inflight = null
    })
  return inflight
}

/** group / 用户切换时清空缓存 */
export function invalidateSkillCache(): void {
  cache = {}
  loadedGroupId = null
  loadedAt = 0
}
